import { AfterViewChecked, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import swal from 'sweetalert2/dist/sweetalert2.js';
import { AuthService } from '../auth.service';
import { BaseComponent } from '../base/base.component';
import { ClassAssignment } from '../data/class-assignment';
import { DataService } from '../data/data.service';
import { IDropdownItem } from '../dropdown-standard/dropdown-standard.component';
import { OrganizationService } from '../organization.service';
import { RedirectService } from '../redirect.service';
import { FormatUtilities } from '../utilities/format-utilities';
import { Location } from '@angular/common';
import { AssignmentSummary } from '../data/assignments-summary';
import { Student } from '../data/student';

@Component({
  selector: 'app-class-assignment',
  templateUrl: './class-assignment.component.html',
  styleUrls: ['./class-assignment.component.scss']
})
export class ClassAssignmentComponent extends BaseComponent implements OnInit, AfterViewChecked {

  constructor(route: ActivatedRoute,
    authService: AuthService,
    organizationService: OrganizationService,
    redirectService: RedirectService,
    router: Router,
    dataService: DataService,
    location: Location)
    { 
      super(route, authService, organizationService, redirectService, router, dataService, location);
    }

  @Input() classId: number;
  @Input() courseCode: string;
  @Input() students: Student[];
  @ViewChild('newAssignment', { static: false }) newAssignmentModal: any;
  @ViewChild('assignmentsTable', { static: false }) assignmentsTable: ElementRef;
  activeModal: NgbModalRef;

  longDateFormat = FormatUtilities.longDateFormatUtc;
  isAddingTasks: boolean = false;
  buttonText: string = 'Add New Assignment';
  classAssignments: AssignmentSummary[] = [];
  assignmentStates: string[];
  assignmentRanges: { startDate: string, endDate: string }[];
  newClassAssignment: AssignmentSummary = new AssignmentSummary();
  activityId: string;
  unit: number;
  organizationId: number;
  classAssignmentReceived: boolean = false;
  activityDescriptions: Map<string, string> = new Map();
  isSaving: boolean = false;
  doneScrolling: boolean = false;

  ngOnInit() {
    this.newClassAssignment = new AssignmentSummary();
    this.route.params.subscribe(params => {
      this.organizationId = params.organizationId;

    });
    this.dataService.getClassAssignments(this.organizationId).subscribe(resp => {
      if (Object.keys(resp).length > 0) {
        this.classAssignments = resp.sort((a,b) => a.StartDate < b.StartDate ? -1 : 1);
        this.classAssignmentReceived = true;
        this.assignmentStates = this.classAssignments.map(assignment => this.getStateForAssignment(assignment));
        this.assignmentRanges = this.classAssignments.map(assignment => ({ startDate: assignment.StartDate, endDate: assignment.EndDate}));
      }
      else {
        this.assignmentStates = [];
        this.assignmentRanges = [];
        this.classAssignmentReceived = true;
      }
    });
  }

  ngAfterViewChecked(): void {
    if (this.assignmentsTable) {
      this.scrollPastCompleted();
    }
  }

  getDisplayState(assignment: AssignmentSummary) {
    if (this.isPostTestAssignment(assignment)) {
      return "post-test";
    }
    return this.getStateForAssignment(assignment);
  }

  getStateForAssignment(assignment: AssignmentSummary) {
    let today = new Date();
    let assignmentStart = new Date(assignment.StartDate);
    if (assignmentStart > today) return "upcoming";
    let assignmentEnd = new Date(assignment.EndDate);
    assignmentEnd.setDate(assignmentEnd.getDate() + 1); // assignments last until the END of EndDate
    if (assignmentEnd < today) return "completed";
    return "in-progress";
  }

  getIconForState(state: string) {
    if(state === "upcoming") return "fas fa-circle upcoming";
    if(state === "completed") return "fas fa-square completed";
    if(state === "in-progress") return "fas fa-star in-progress";
    return "fas fa-question-mark";
  }

  scrollPastCompleted() {
    if(this.doneScrolling || this.classAssignments.length === 0) return;
    const completedCount = this.assignmentStates.filter(state => state === 'completed').length;
    this.assignmentsTable.nativeElement.scrollTop = completedCount * 50;
    this.doneScrolling = true;
  }

  addNewAssignment() {
    this.newAssignmentModal.openForNew();
  }

  assignmentCreated(result: ClassAssignment) {
    this.goToRoute(result, `/classes/${this.organizationId}/class/${this.classId}/assignments/${result.AssignmentID}`, {});
  }

  hideActiveModal() {
    this.activeModal.close();
  }

  getOtherAssignmentRanges(startDate: string, endDate: string) {
    return this.assignmentRanges.filter(range => range.startDate !== startDate && range.endDate !== endDate);
  }

  deleteAssignment(event: Event, assignment: AssignmentSummary) {
    event.stopPropagation();
    swal.fire({
      title: 'Are you sure?',
      html: `This will permanently delete the assignment: <br> ${assignment.Name}`,
      type: 'warning',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Delete",
      customClass: {
        confirmButton: 'swal-button--danger',
      }
    }).then(res => {
      if (res.value) {
        this.dataService.deleteClassAssignment(this.organizationId, assignment.ID).subscribe({
          next: o => window.location.reload(),
          error: err => swal.fire('Oops!', err.message)
        });
      }
    });
  }

  saveAssignment(event: Event, assignment: AssignmentSummary) {
    this.startSaveObj(event);
    this.dataService.updateAssignmentSummary(this.organizationId, assignment).subscribe({
      next: o => {
        this.handleSaveSuccess();
        this.assignmentRanges = this.classAssignments.map(assignment => ({ startDate: assignment.StartDate, endDate: assignment.EndDate}));
      },
      error: err => {
        swal.fire('Oops!', err.message);
        this.handleSaveError();
      }
    });
  }

  cancelEditAssignment(event: Event, assignment: AssignmentSummary) {
    event.stopPropagation();
    if (
      assignment.StartDate == this.origEditObj["StartDate"]
      && assignment.EndDate == this.origEditObj["EndDate"]
      && assignment.Name == this.origEditObj["Name"]
    ) {
      this.cancelEditObj(event, assignment);
      return;
    }
    swal.fire({
      title: 'Are you sure?',
      text: `This discards all of your changes to this assignment`,
      type: 'warning',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Discard Changes",
      customClass: {
        confirmButton: 'swal-button--danger',
      }
    }).then(res => {
      if (res.value) {
        this.cancelEditObj(event, assignment);
      }
    });
  }

  showReport(event: Event, assignment: AssignmentSummary) {
    event.stopPropagation();
    this.goToRoute(assignment, `/classes/${this.organizationId}/class/${this.classId}/assignments/${assignment.ID}/assignment-report`, {});
  }

  private isPostTestAssignment(assignment: AssignmentSummary): boolean {
    const POST_TEST_FULL = "Post Test Full Test ca7267ca-48f4-4e6b-933c-4e76969ab9ba";
    const POST_TEST_HALF = "Post Test First Half ca7267ca-48f4-4e6b-933c-4e76969ab9ba";
    
    return assignment?.Name === POST_TEST_FULL || 
           assignment?.Name === POST_TEST_HALF;
  }

  goToAssignmentDetails(event: Event, assignment: AssignmentSummary) {
    if (this.isPostTestAssignment(assignment)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    
    this.router.navigate([`/classes/${this.organizationId}/class/${this.classId}/assignments/${assignment.ID}`]);
  }
}

export class UnitNumber implements IDropdownItem {
  constructor(public Value: number) { }
  GetDescription(): string { return this.Value.toString(); }
}